import {
  Avatar,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import React, { useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import Autodesk from 'assets/images/auth/autodesk.webp';
import Excel from 'assets/images/auth/excel.png';
import Redas from 'assets/images/auth/redas_result.webp';
import SGBuildex from 'assets/images/auth/sgbuildex_result.webp';
import GoogleDrive from 'assets/images/auth/googledrive_result.webp';
import useMigrateStore from '../../../store/useMigrate';
import { useMigrationSchedule } from '../../../hooks/useMigration';

const InfoRow = ({ label, value }) => (
  <Stack direction="column" justifyContent="space-between">
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body1" fontWeight={400}>
      {value || '-'}
    </Typography>
  </Stack>
);

const GoogleContent = ({ data, mode }) => {
  return (
    <>
      <Stack direction="row" spacing={1.5} alignItems="center" mb={2}>
        <Avatar src={GoogleDrive} sx={{ width: 32, height: 32 }} />
        <Typography fontWeight="bold">{mode} (Google Drive)</Typography>
      </Stack>

      <Stack spacing={1}>
        <InfoRow label="Connection Name" value={data?.connection_owner_name} />
        <InfoRow label="Item ID" value={data?.data.id} />
        <InfoRow label="Type" value={data?.type} />
        <InfoRow label="Name" value={data?.data?.name} />
      </Stack>
    </>
  );
};

const AutodeskContent = ({ data, mode }) => {
  return (
    <>
      <Stack direction="row" spacing={1.5} alignItems="center" mb={2}>
        <Avatar src={Autodesk} sx={{ width: 32, height: 32 }} />
        <Typography fontWeight="bold">{mode} (ACC)</Typography>
      </Stack>

      <Stack spacing={1}>
        <InfoRow label="Connection Name" value={data?.connection_owner_name} />
        <InfoRow label="Project ID" value={data?.project_id} />
        <InfoRow label="Item ID" value={data?.data.id} />
        <InfoRow label="Type" value={data?.type} />
        <InfoRow label="Name" value={data?.data.attributes?.name} />
      </Stack>
    </>
  );
};

function renderContentProvider(param, data) {
  if (data?.provider === 'googledrive') {
    return <GoogleContent mode={param} data={data} />;
  }
  if (data?.provider === 'autodesk') {
    return <AutodeskContent mode={param} data={data} />;
  }
}

function ScheduleFormDialog({ open, onClose, onConfirm }) {
  const { sourceProviderData, destinationProviderData, handleSourceProviderData, handleDestinationProviderData } = useMigrateStore();
  const { mutate: migrateDataSchedule, isPending: isMigrateDataSchedule } = useMigrationSchedule();
  //   const [scheduleName, setscheduleName] = useState('');
  //   const [timeInterval, setTimeInterval] = useState('');
  const formik = useFormik({
    initialValues: {
      scheduleName: '',
      timeInterval: ''
    },
    validationSchema: Yup.object({
      scheduleName: Yup.string().required('Schedule name is required'),
      timeInterval: Yup.string().required('Time interval is required')
    }),
    onSubmit: (values) => {
      handleSubmit(values); // pass data to parent
    }
  });
  console.log({ sourceProviderData, destinationProviderData });

  const handleSubmit = (data) => {
    const payload = {
      schedule_name: data.scheduleName,
      frequency: data.timeInterval,
      payload: {
        source: sourceProviderData,
        destination: destinationProviderData
      }
    };
    migrateDataSchedule(payload, {
      onSuccess: () => {
        handleSourceProviderData(null);
        handleDestinationProviderData(null);
        onConfirm(sourceProviderData, destinationProviderData);
      },
      onError: (error) => {
        // console.error('MUTATION ERROR', error);
      }
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle fontWeight="bold">Schedule Migration</DialogTitle>

      <DialogContent>
        <Stack direction="row" spacing={3} alignItems="stretch" mt={1}>
          {/* SOURCE */}
          <Card
            elevation={0}
            sx={{
              flex: 1,
              borderRadius: 2,
              border: '1px solid',
              borderColor: '#cccccc',
              background: '#f2f2f2'
            }}
          >
            <CardContent>{renderContentProvider('Source', sourceProviderData)}</CardContent>
          </Card>

          {/* ARROW */}
          <Stack justifyContent="center">
            <ArrowForwardIcon color="action" />
          </Stack>

          {/* DESTINATION */}
          <Card
            elevation={0}
            sx={{
              flex: 1,
              borderRadius: 2,
              border: '1px solid',
              borderColor: '#cccccc',
              background: '#f2f2f2'
            }}
          >
            <CardContent>{renderContentProvider('Destination', destinationProviderData)}</CardContent>
          </Card>
        </Stack>

        <Divider sx={{ my: 3 }} />
        <form onSubmit={formik.handleSubmit}>
          <Stack spacing={2} mb={3}>
            <TextField
              label="Schedule Name"
              name="scheduleName"
              fullWidth
              required
              value={formik.values.scheduleName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.scheduleName && Boolean(formik.errors.scheduleName)}
              helperText={formik.touched.scheduleName && formik.errors.scheduleName}
            />

            <FormControl fullWidth error={formik.touched.timeInterval && Boolean(formik.errors.timeInterval)}>
              <InputLabel>Time Interval</InputLabel>
              <Select
                name="timeInterval"
                label="Time Interval"
                value={formik.values.timeInterval}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              >
                <MenuItem value="HOURLY">Hourly</MenuItem>
                <MenuItem value="DAILY">Daily</MenuItem>
                <MenuItem value="WEEKLY">Weekly</MenuItem>
                <MenuItem value="MONTHLY">Monthly</MenuItem>
              </Select>
              <FormHelperText>{formik.touched.timeInterval && formik.errors.timeInterval}</FormHelperText>
            </FormControl>
          </Stack>

          <Button type="submit" sx={{ mt: 2, fontWeight: 'bold' }} fullWidth variant="contained">
            CONFIRM SCHEDULE
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default ScheduleFormDialog;
